import React from "react"
import { UserAccount, UserEmail, UserType } from "../../profileTypes"
import { Box, Divider, Grid, ListItemButton, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useApiMutation } from "../../../../core/api/useApiMutation"
import { changeAccount } from "../../profileApi"
import { enqueueSnackbar } from "notistack"
import { useAppDispatch } from "../../../store/storeHooks"
import { login } from "../../../login/loginSlice"

const useStyles = makeStyles()((theme) => ({
    userListContainer: {
        padding: "2rem",
    },
    listItem: {
        padding: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    subtitle: {
        textAlign: "center",
        color: theme.palette.tailwind.neutral[600],
    },
    emailEmptyState: {
        fontWeight: 600,
        fontSize: "1.5rem",
    },
    icon: {
        padding: "0.2rem 1rem",
        color: theme.palette.tailwind.neutral[600],
    },
    emptyContent: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: "3rem",
    },
}))

export function UsersList({ users, user }: { users: Array<UserType>; user: UserEmail }) {
    const { classes } = useStyles()
    const dispatch = useAppDispatch()

    const { mutateAsync: callChangeAccount } = useApiMutation(changeAccount, {
        async onSuccess(_params, data) {
            enqueueSnackbar({
                variant: "success",
                message: data.authenticationResult.message,
            })
            dispatch(
                login({
                    authToken: data.authentication.token,
                    user: data.user,
                }),
            )
        },
    })

    const onUserClick = async (user: UserAccount) => {
        await callChangeAccount({ uid: user.uid })
    }

    if (!users || users.length === 0) {
        return (
            <Box className={classes.emptyContent}>
                <LocalizedStrict id="change-user-account-dialog-empty-content-subtitle">
                    <Typography className={classes.subtitle}>
                        No other user accounts were found with the email:
                    </Typography>
                </LocalizedStrict>
                <Typography className={classes.emailEmptyState}>{user.email}</Typography>
            </Box>
        )
    }

    const totalUserAccounts = users.reduce((total, userType) => {
        return total + userType.userAccounts.length
    }, 0)

    return (
        <Grid container direction="column">
            <Stack direction="column" alignItems="center" gap=".5rem">
                <LocalizedStrict
                    id="change-user-account-dialog-users-list-available-accounts-subtitle"
                    vars={{
                        totalUserAccounts,
                        email: user.email,
                    }}
                >
                    <Typography variant="body1">
                        You have {totalUserAccounts} other user accounts with the e-mail: {user.email}
                    </Typography>
                </LocalizedStrict>
                <LocalizedStrict id="change-user-account-dialog-users-list-swift-to-another-account">
                    <Typography variant="body1">Swift to another account below:</Typography>
                </LocalizedStrict>
            </Stack>
            <Stack className={classes.userListContainer} direction="column" gap="2rem">
                {users?.map((userType, index) => (
                    <Box key={index}>
                        <Typography paddingBottom=".5rem" variant="subtitle1">
                            {userType.name}
                        </Typography>
                        <Divider />

                        {userType.userAccounts.map((user) => (
                            <React.Fragment key={user.uid}>
                                <ListItemButton onClick={() => onUserClick(user)} className={classes.listItem}>
                                    <Box display="flex">
                                        <Box>
                                            <Typography variant="body1">
                                                {user.displayName} ({user.uid})
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <FontAwesomeIcon className={classes.icon} icon={faChevronRight} />
                                    </Box>
                                </ListItemButton>

                                <Divider />
                            </React.Fragment>
                        ))}
                    </Box>
                ))}
            </Stack>
        </Grid>
    )
}
